/* eslint-disable consistent-return */
import {
  DeleteOutlined,
  EyeOutlined, LoadingOutlined, PlusOutlined,
  PrinterOutlined
} from '@ant-design/icons';
import {
  Button,
  message,
  Modal,
  Space, Table, Tooltip, Typography
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import {
  FilterDataProps,
  FilterValueProps,
  mappingFilterFields,
  mappingFilterToQuery,
  mappingParamsFilter,
  mappingQueryParamsFilter
} from 'common/components/PageTable/filter';
import PopupGuideSettingPopups from 'common/components/PopupGuideSettingPopups';
import { getLocalStorage, setLocalStorage } from 'common/services/common/storage';
import { getExportByCodeService } from 'common/services/extends/exports';
import {
  deleteStaffService,
  exportStaffService,
  getAllStaffService,
  getProjectStaffService,
  printStaffService
} from 'common/services/extends/staff';
import { StaffContractEnum, StaffProjectResponse } from 'common/services/extends/staff/types';
import LOCAL_STORAGE, { NoTaxCode, ROUTE_PATHS } from 'common/utils/constant';
import { downloadFile, isPopupBlocked } from 'common/utils/functions';
import roles, { getPermission } from 'configs/roles';

export type BankAccount = {
  bankOwner: string;
  bankAccount: string;
  isDefault: boolean;
  // bankCode: string;
  bankName: OptionType | null;
  bankBranch: OptionType | null;
  city: OptionType | null;
  isActive: boolean
};

export type TableDataTypes = {
  id: number;
  fullname: string;
  dob?: string;
  taxCode: string | null;
  phoneNumber: string;
  idCardNumber: string;
  idCardIssuedAt?: string;
  idCardIssuedBy?: string;
  permanentAddress: string;
  contractType?: OptionType;
  socialInsuranceNumber: string;
  socialInsuranceJoinDate?: string;
  socialInsuranceImage: string;
  bankAccounts: Array<BankAccount>;
};

const EmployeeManagement: React.FC<ActiveRoles> = ({
  roleDelete, roleCreate, roleIndex, roleOther
}) => {
  /* Hooks */
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  /* Selectors */
  const {
    defaultPageSize, advancedFilter
  } = useAppSelector((state) => state.system);

  /* Search Params */
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => {
    const paramsObj = { ...Object.fromEntries(searchParams.entries()) };
    if (searchParams.has('page')) delete paramsObj.page;
    return paramsObj;
  }, [searchParams]);
  const pageParam = searchParams.get('page');

  /* States */
  const [currentPage, setCurrentPage] = useState(Number(pageParam));
  const [currentView, setCurrentView] = useState(defaultPageSize);
  const [keyword, setKeyword] = useState('');
  const [selectedFilterList, setSelectedFilterList] = useState<
    FilterValueProps[]>(mappingQueryParamsFilter(params));
  const [downloading, setDownloading] = React.useState(false);
  const [isPopupBlock, setIsPopupBlock] = React.useState({
    status: false,
    link: ''
  });
  const [checkDownload, setCheckDownload] = React.useState({
    status: false,
    code: ''
  });
  const [loadingGetProjectStaff, setLoadingGetProjectStaff] = useState(false);

  /* ref */
  const clickedDownloadRef = React.useRef<boolean>(false);
  const checkStatusExportRef = React.useRef<boolean>(false);
  const isFirstRender = React.useRef(true);

  const returnParamsFilter = useMemo(
    () => mappingParamsFilter(selectedFilterList),
    [selectedFilterList]
  );

  /* Queries */
  const { data: employeeRes, isLoading } = useQuery(
    ['get-employees', currentPage, currentView, selectedFilterList, keyword],
    () => getAllStaffService({
      page: currentPage,
      limit: currentView,
      ...(keyword && { keyword }),
      ...returnParamsFilter
    }),
    {
      enabled: currentPage > 0,
    }
  );

  const { mutate: deleteMutate, isLoading: deleteLoading } = useMutation(
    ['delete-employees'],
    deleteStaffService,
    {
      onSuccess: () => {
        // Invalidate the query to refetch the data
        queryClient.invalidateQueries(['get-employees', currentPage, currentView]);
        message.success(t('message.deleteSuccess'));
      },
      onError: () => {
        message.error(t('message.deleteError'));
      }
    }
  );

  const handleDelete = (ids: number[]) => {
    deleteMutate(ids);
  };

  const handleBeforeDelete = async (ids: number[]) => {
    try {
      setLoadingGetProjectStaff(true);
      const data = await getProjectStaffService(ids);

      if (!data.length) {
        Modal.confirm({
          className: 't-pagetable_deleteRecordModal',
          autoFocusButton: 'cancel',
          okText: t('system.ok'),
          cancelText: t('system.cancel'),
          cancelButtonProps: {
            type: 'primary',
          },
          okButtonProps: {
            type: 'default',
          },
          title: t('message.confirmDeleteRecord'),
          onOk: () => {
            handleDelete(ids);
          },
        });
      } else {
        Modal.warning({
          width: 1000,
          cancelButtonProps: {
            hidden: true,
          },
          title: <Typography.Text type="danger">{t('employee.cannotDeleteBecauseEvolved')}</Typography.Text>,
          content: (
            <Table
              dataSource={data}
              size="small"
              scroll={{
                x: 'max-content'
              }}
              pagination={false}
              bordered
              columns={[
                {
                  title: t('employee.fullname'),
                  dataIndex: ['staffData', 'fullname'],
                  key: 'staffName',
                },
                {
                  title: t('program.code'),
                  dataIndex: ['projectData', 'code'],
                  key: 'projectCode',
                },
                {
                  title: t('program.name'),
                  dataIndex: ['projectData', 'name'],
                  key: 'projectName',
                },
                {
                  title: t('timesheet.timeKeepingName'),
                  dataIndex: ['payrollPeriodData', 'name'],
                  key: 'payrollPeriodName',
                },
              ] as ColumnsType<StaffProjectResponse>}
            />
          )
        });
      }
    } catch (error) {
      message.error(t('message.anErrorHasOccurred'));
    } finally {
      setLoadingGetProjectStaff(false);
    }
  };

  const { mutate: printMuate, isLoading: isLoadingPrint } = useMutation(
    ['print-employee'],
    (id: number) => printStaffService(id),
    {
      onSuccess: (data) => {
        window.open(data.link, '_blank');
        message.success(t('message.exportSuccess'));
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  const onHandlePrint = (id: number) => {
    printMuate(id);
  };

  const {
    mutate: exportStaffReportMutate,
  } = useMutation(
    ['export-staff-report'],
    exportStaffService,
    {
      onSuccess: async (data: any) => {
        if (data?.downloadUrl) {
          const blocked = getLocalStorage(LOCAL_STORAGE.BLOCKED_POPUPS);
          if (blocked) {
            setIsPopupBlock({
              status: true,
              link: data.downloadUrl
            });
            return;
          }
          message.success(t('message.exportSuccess'));
          setCheckDownload({
            status: false,
            code: ''
          });
          window.open(data.downloadUrl, '_blank');
        } else {
          setCheckDownload({
            status: true,
            code: data.code || ''
          });
        }
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  /* Datas */

  const tableData = useMemo(() => employeeRes?.data.map((item) => {
    const {
      socialInsuranceImage,
      contractType,
    } = item.staffData;
    return {
      ...item.staffData,
      contractType: {
        value: contractType,
        label: StaffContractEnum[contractType]
      },
      socialInsuranceImage: socialInsuranceImage || '',
      bankAccounts: item.bankData.map((bank) => ({
        bankOwner: bank.accountName,
        bankAccount: bank.accountNumber,
        isDefault: bank.accountDefault,
        isActive: bank.isActive,
        bankName: bank.bankName ? {
          value: bank.bankName,
          label: bank.bankName
        } : null,
        bankBranch: bank.branchName ? {
          value: bank.branchName,
          label: bank.branchName
        } : null,
        city: bank.province ? {
          value: bank.province.code,
          label: bank.province.name
        } : null
      }))
    };
  }) || [], [employeeRes]);

  /* Variables */
  const columns: ColumnsType<TableDataTypes> = [
    {
      title: t('employee.fullname'),
      dataIndex: 'name',
      key: 'name',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.fullname}
        </Typography.Text>
      ),
    },
    {
      title: t('employee.bankAccount'),
      dataIndex: 'bankAccount',
      key: 'bankAccount',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.bankAccounts.find((bank) => bank.isDefault)?.bankAccount}
        </Typography.Text>
      ),
    },
    {
      title: t('employee.identityNumber'),
      dataIndex: 'identityNumber',
      key: 'identityNumber',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.idCardNumber}
        </Typography.Text>
      ),
    },
    {
      title: t('system.phone'),
      dataIndex: 'phone',
      key: 'phone',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.phoneNumber}
        </Typography.Text>
      ),
    },
    {
      title: t('employee.taxNumber'),
      dataIndex: 'taxNum',
      key: 'taxNum',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.taxCode || NoTaxCode.label}
        </Typography.Text>
      ),
    },
    {
      title: t('system.action'),
      key: 'action',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (_: string, _data: TableDataTypes) => (
        <Space>
          <Tooltip title={t('employee.printContract')}>
            <Button
              disabled={!roleOther.includes(roles.PROJECT_PRINT_CONTRACT) || isLoadingPrint}
              icon={<PrinterOutlined />}
              onClick={() => onHandlePrint(_data.id)}
            />
          </Tooltip>
          <Tooltip title={t('system.viewDetail')}>
            <Button
              disabled={!roleIndex}
              icon={<EyeOutlined />}
              onClick={() => navigate(`${ROUTE_PATHS.EMPLOYEE_DETAIL}?id=${_data.id}`)}
            />
          </Tooltip>
          <Tooltip title={t('system.delete')}>
            <Button
              disabled={!roleDelete}
              icon={<DeleteOutlined />}
              loading={loadingGetProjectStaff}
              onClick={() => {
                handleBeforeDelete([_data.id]);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleSearch = (val: string) => {
    setKeyword(val);
  };

  const handleSetCurrentPage = (page: number) => {
    setCurrentPage(page);
    setSearchParams({
      ...mappingFilterToQuery(selectedFilterList), page: page.toString()
    });
  };

  const handleSetCurrentView = (view: number) => {
    setCurrentView(view);
  };

  const handleFilter = (data: FilterValueProps) => {
    const typeFilter = String(data.filter).split('.')[1];
    if ((typeFilter === 'isNull' || typeFilter === 'isNotNull') && selectedFilterList.find((item) => item.key === data.key)) {
      return;
    }
    const counter = selectedFilterList.filter(
      (item) => item.field === data.field && item.filter === data.filter
    ).length;
    setSelectedFilterList([...selectedFilterList, { ...data, index: counter }]);
    handleSetCurrentPage(1);
  };

  const handleDeleteFilter = (key: string, index?: number) => {
    const tempList = selectedFilterList.slice();
    setSelectedFilterList(tempList.filter((item) => !(item.key === key && item.index === index)));
    handleSetCurrentPage(1);
  };

  const filterFields: FilterDataProps[] = useMemo(
    () => mappingFilterFields('staff', advancedFilter),
    [advancedFilter]
  );

  const handleDownloadStaffReport = async () => {
    try {
      if (clickedDownloadRef.current) {
        return;
      }
      clickedDownloadRef.current = true;

      setDownloading(true);
      setCheckDownload({
        status: false,
        code: ''
      });

      exportStaffReportMutate({
        ...returnParamsFilter
      });
    } catch (error) {
      message.error(t('message.exportError'));
      setDownloading(false);
      clickedDownloadRef.current = false;
      setCheckDownload({
        status: false,
        code: ''
      });
      setIsPopupBlock({
        status: false,
        link: ''
      });
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setCurrentPage(1);
    setSearchParams({
      ...mappingFilterToQuery(selectedFilterList),
      page: '1'
    }, { replace: true });
  }, [keyword, setSearchParams, selectedFilterList]);

  useEffect(() => {
    const blocked = isPopupBlocked('');
    if (blocked) {
      setLocalStorage(LOCAL_STORAGE.BLOCKED_POPUPS, 'true');
    } else {
      window.localStorage.removeItem(LOCAL_STORAGE.BLOCKED_POPUPS);
    }
  }, []);

  useEffect(() => {
    if (pageParam) {
      setCurrentPage(Number(pageParam));
    } else {
      setCurrentPage(1);
    }
  }, [pageParam]);

  useEffect(() => {
    if (checkDownload.status && checkDownload.code) {
      let numberOfChecks = 0;
      const interval = setInterval(async () => {
        if (checkStatusExportRef.current) {
          console.log('Already checking status, skipping this interval.');
          return;
        }

        // Limit the number of checks to prevent infinite loops
        if (numberOfChecks >= 9) {
          clearInterval(interval);
          message.error(t('message.exportError'));
          setCheckDownload({
            status: false,
            code: ''
          });
          setDownloading(false);
          clickedDownloadRef.current = false;
          return;
        }
        console.log('Checking download status...');
        try {
          checkStatusExportRef.current = true;
          numberOfChecks += 1;
          const response = await getExportByCodeService(checkDownload.code);
          if (response?.status === 'failed') {
            clearInterval(interval);
            message.error(t('message.exportError'));
            setCheckDownload({
              status: false,
              code: ''
            });
            setIsPopupBlock({
              status: false,
              link: ''
            });
            setDownloading(false);
            clickedDownloadRef.current = false;
            return;
          }
          if (response?.downloadUrl && response.status === 'success') {
            const blocked = getLocalStorage(LOCAL_STORAGE.BLOCKED_POPUPS);
            if (blocked && response) {
              setIsPopupBlock({
                status: true,
                link: response?.downloadUrl
              });
              clearInterval(interval);
              setCheckDownload({
                status: false,
                code: ''
              });
              setDownloading(false);
              clickedDownloadRef.current = false;
              return;
            }
            clearInterval(interval);
            // window.open(response.downloadUrl, '_blank');
            downloadFile(response.downloadUrl, 'danh-sach-nhan-vien-parttime');
            setCheckDownload({
              status: false,
              code: ''
            });
            setDownloading(false);
            clickedDownloadRef.current = false;
          } else {
            console.log('Download not ready yet, checking again...');
          }
        } catch (error) {
          clearInterval(interval);
          setIsPopupBlock({
            status: false,
            link: ''
          });
          console.error('Error checking download status:', error);
          message.error(t('message.exportError'));
          setCheckDownload({
            status: false,
            code: ''
          });
          setDownloading(false);
          clickedDownloadRef.current = false;
        } finally {
          checkStatusExportRef.current = false;
        }
      }, 3000); // Check every 3 seconds

      return () => clearInterval(interval); // Clear interval on unmount
    }
  }, [checkDownload, t]);

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.employee')}
        rightHeader={(
          <Space>
            <Button
              type="primary"
              disabled={!roleCreate}
              onClick={() => navigate(ROUTE_PATHS.EMPLOYEE_DETAIL)}
            >
              <PlusOutlined />
              {t('system.create')}
            </Button>
            <Button
              disabled={!getPermission(roleOther, roles.EXPORT_STAFF)}
              onClick={handleDownloadStaffReport}
            >
              {
                downloading ? (
                  <Space>
                    {t('exportReports.checkingDownload')}
                    <LoadingOutlined style={{ fontSize: 16 }} />
                  </Space>
                ) : t('exportReports.export')
              }
            </Button>
          </Space>
        )}
      />
      <div className="t-mainlayout_wrapper">
        <PageTable
          noCheckbox
          isLoading={isLoading || deleteLoading}
          handleSearch={handleSearch}
          filtersDataTable={{
            handleFilter,
            selectedFilterList,
            handleDeleteFilter,
          }}
          tableProps={{
            initShowColumns: ['name', 'bankAccount', 'identityNumber', 'phone', 'taxNum', 'action'],
            columns,
            pageData: tableData,
            currentPage,
            pageSize: currentView,
            handleSetCurrentPage,
            handleSetCurrentView,
            total: employeeRes?.meta.total || 0,
            noBaseCol: true,
            noDeleteLanguage: true,
            filterFields
          }}
        />
      </div>
      <PopupGuideSettingPopups
        fileName="danh-sach-nhan-vien-parttime"
        isLoading={downloading}
        isPopupBlock={isPopupBlock}
        onClose={() => setIsPopupBlock({ status: false, link: '' })}
      />
    </>
  );
};

export default EmployeeManagement;
